import { SECTION } from "constants/SettingsConstants";
import { useLienFileData } from "pages/liens/hooks/useLienFileData";
import type { LienValidation } from "pages/liens/LienTypes";
import { memo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useGetAmendedReviewValidationsByIdQuery } from "services/ar-screening-api";
import { useGetCostProjectionValidationByIdQuery } from "services/cost-projection-api";
import { useGetLienValidationByIdQuery } from "services/lien-api";
import { useGetOrganizationSettingsQuery } from "services/misc-api";
import { useGetMSAValidationByIdQuery } from "services/msa-api";
import type { CostProjectionValidation, MSAValidation } from "types/DataTypes";
import { useSelector } from "utils/redux";

import StatusBlock from "./StatusBlock";
import type { PageType, StatusColor } from "./StatusHeaderUtils";
import { getPageType, getStatusTitles } from "./StatusHeaderUtils";

// the MSA flavors that share the /future-medical/msa/ pages
type MsaSectionType =
  | typeof SECTION.MSA
  | typeof SECTION.MSA_111
  | typeof SECTION.NTMSA
  | typeof SECTION.PRE_MSA;

const MSA_SECTION_TYPES: readonly MsaSectionType[] = [
  SECTION.MSA,
  SECTION.MSA_111,
  SECTION.NTMSA,
  SECTION.PRE_MSA,
];

const isMsaSectionType = (
  type: string | null | undefined,
): type is MsaSectionType =>
  (MSA_SECTION_TYPES as readonly string[]).includes(type ?? "");

// fixed header area used in KAboodleApp, shown in MSA and Lien pages, shows status of each step in the current MSA/Lien
const KaboodleStatusHeader = memo(({ className }: { className?: string }) => {
  const location = useLocation();
  const { data: organizationSettings } = useGetOrganizationSettingsQuery();
  const { msaId } = useParams<{ msaId?: string }>();
  const { lienId } = useParams<{ lienId?: string }>();
  const { lienFileId } = useParams<{ lienFileId?: string }>();
  const { costProjectionId } = useParams<{ costProjectionId?: string }>();
  const { lcpId } = useParams<{ lcpId?: string }>();
  const { mcpId } = useParams<{ mcpId?: string }>();
  const { reviewId } = useParams<{ reviewId?: string }>();
  const resolvedCostProjectionId = costProjectionId || lcpId || mcpId;

  // get lien file data to get file id for lien invoices
  const { lienFileData } = useLienFileData();

  const pageType = getPageType(location.pathname);
  const isAmendedReview = location.pathname.includes("/amended-review/");

  // every MSA flavor (MSA, NTMSA, MSA-111, Pre-MSA) lives under
  // /future-medical/msa/, so the URL can't tell us which one we're on; the
  // loaded service can, and NTMSA / MSA-111 / Pre-MSA have no CMS segments
  const currentService = useSelector(
    (state) => state.futureMedical.currentService,
  );
  const currentServiceType = currentService?.type;
  const currentMsaType: MsaSectionType =
    String(currentService?.id) === msaId && isMsaSectionType(currentServiceType)
      ? currentServiceType
      : SECTION.MSA;

  const { data: msaValidation } = useGetMSAValidationByIdQuery<{
    data: MSAValidation;
  }>(
    { msaId },
    {
      skip:
        (pageType !== SECTION.MSA && pageType !== SECTION.AMENDED_REVIEW) ||
        !msaId,
    },
  );

  const { data: lienValidation } = useGetLienValidationByIdQuery<{
    data: LienValidation;
  }>({ lienId }, { skip: pageType !== SECTION.LIENS || !lienId });

  const { data: costProjectionValidation } =
    useGetCostProjectionValidationByIdQuery(
      { costProjectionId: resolvedCostProjectionId },
      {
        skip:
          (pageType !== SECTION.LCP && pageType !== SECTION.MCP) ||
          !resolvedCostProjectionId,
      },
    );

  const { data: amendedReviewValidation } =
    useGetAmendedReviewValidationsByIdQuery(
      { reviewId },
      {
        skip: pageType !== SECTION.AMENDED_REVIEW_SCREENING || !reviewId,
      },
    );

  const getStatusTitlesForMSA = (
    isAmendedReview: boolean,
    msaId: string,
    msaValidation: MSAValidation,
    pageType: PageType,
  ) => {
    if (!msaId || !msaValidation) return {};

    const type = isAmendedReview ? SECTION.AMENDED_REVIEW : currentMsaType;

    if (isAmendedReview || pageType === SECTION.MSA) {
      return getStatusTitles(
        {
          type,
          validation: msaValidation,
          id: msaId,
        },
        organizationSettings?.quickbooks_integration_enabled ?? true,
      );
    }

    return {};
  };

  const getStatusTitlesForScreening = (
    pageType: PageType,
    reviewId: string | undefined,
    validation: MSAValidation | undefined,
  ) => {
    if (
      pageType !== SECTION.AMENDED_REVIEW_SCREENING ||
      !reviewId ||
      !validation
    ) {
      return {};
    }

    return getStatusTitles(
      {
        type: SECTION.AMENDED_REVIEW_SCREENING,
        validation,
        id: reviewId,
      },
      organizationSettings?.quickbooks_integration_enabled ?? true,
    );
  };

  const getStatusTitlesForCostProjection = (
    pageType: PageType,
    costProjectionId: string | undefined,
    validation: CostProjectionValidation | undefined,
  ) => {
    if (
      (pageType !== SECTION.MCP &&
        pageType !== SECTION.LCP &&
        !costProjectionId) ||
      !validation
    ) {
      return {};
    }

    return getStatusTitles(
      {
        type: pageType as "LCP" | "MCP",
        validation,
        id: costProjectionId,
      },
      organizationSettings?.quickbooks_integration_enabled ?? true,
    );
  };

  const getStatusTitlesForLiens = (
    pageType: PageType,
    lienFileId: string | undefined,
    lienId: string | undefined,
    validation: LienValidation | undefined,
    fileId: string | undefined,
  ) => {
    if (pageType !== SECTION.LIENS || !lienId || !lienFileId || !validation) {
      return {};
    }

    return getStatusTitles(
      {
        type: SECTION.LIENS,
        validation,
        lienFileId,
        id: lienId,
        fileId: fileId,
      },
      organizationSettings?.quickbooks_integration_enabled ?? true,
    );
  };

  const determineStatusTitles = () => {
    // MSA and Amended Review
    if (msaId && msaValidation) {
      if (isAmendedReview || pageType === SECTION.MSA) {
        return getStatusTitlesForMSA(
          isAmendedReview,
          msaId,
          msaValidation,
          pageType,
        );
      }
    }

    // Amended Review Screening
    if (
      pageType === SECTION.AMENDED_REVIEW_SCREENING &&
      reviewId &&
      amendedReviewValidation
    ) {
      return getStatusTitlesForScreening(
        pageType,
        reviewId,
        amendedReviewValidation,
      );
    }

    // Cost Projections (MCP/LCP)
    if (
      (pageType === SECTION.MCP || pageType === SECTION.LCP) &&
      resolvedCostProjectionId &&
      costProjectionValidation
    ) {
      return getStatusTitlesForCostProjection(
        pageType,
        resolvedCostProjectionId,
        costProjectionValidation,
      );
    }

    // Liens
    if (pageType === SECTION.LIENS && lienId && lienValidation) {
      return getStatusTitlesForLiens(
        pageType,
        lienFileId,
        lienId,
        lienValidation,
        lienFileData?.file_id,
      );
    }

    return {};
  };

  const statusTitles = determineStatusTitles();

  return (
    <header className={`status-header ${className || ""}`}>
      <div className="status-timeline flex">
        {Object.entries(statusTitles).map(([title, { status, url }]) => (
          <StatusBlock
            key={title}
            title={title}
            status={status as StatusColor}
            url={url}
          />
        ))}
      </div>
    </header>
  );
});

export default KaboodleStatusHeader;
